/**
 * Cyber Academy (internal Moodle) origin, resolved per deployment.
 *
 * A campaign journey step references a JourneyMap (which is what completion is checked against), but
 * the *content* for that step is a Cyber Academy course served from the Cyber Academy site — a
 * different origin per deployment so authors testing on dev/uat hit that tier's courseware, not prod:
 *   - dev / development → https://dev.cyberacademy.cisco.com
 *   - uat               → https://uat.cyberacademy.cisco.com
 *   - production        → https://cyberacademy.cisco.com
 *
 * The deployment is read from `REACT_APP_DEPLOYMENT`, falling back to `REACT_APP_BRANCH_NAME` — the
 * var the Docker builds actually set (`development` / `uat`), so a branch build resolves correctly
 * without a separate deployment var. `REACT_APP_INTERNAL_MOODLE_ORIGIN`, if set, overrides all of it
 * (mirrors moodleHelpers). Note the existing `moodleHelpers.getInternalMoodleOrigin` always returns
 * the prod origin; this is the deployment-aware variant the campaign surface uses.
 */

const PROD_CYBER_ACADEMY_ORIGIN = 'https://cyberacademy.cisco.com';
const UAT_CYBER_ACADEMY_ORIGIN = 'https://uat.cyberacademy.cisco.com';
const NONPROD_CYBER_ACADEMY_ORIGIN = 'https://dev.cyberacademy.cisco.com';

const getDeployment = (): string =>
  (process.env.REACT_APP_DEPLOYMENT || process.env.REACT_APP_BRANCH_NAME || '')
    .trim()
    .toLowerCase();

const isProductionDeployment = (): boolean => {
  const d = getDeployment();
  return d === 'production' || d === 'prod';
};

const isUatDeployment = (): boolean => getDeployment() === 'uat';

/** The Cyber Academy origin for the current deployment (no trailing slash). */
export function cyberAcademyOrigin(): string {
  const configured = (process.env.REACT_APP_INTERNAL_MOODLE_ORIGIN || '').trim().replace(/\/+$/, '');
  if (configured) return configured;
  if (isProductionDeployment()) return PROD_CYBER_ACADEMY_ORIGIN;
  if (isUatDeployment()) return UAT_CYBER_ACADEMY_ORIGIN;
  return NONPROD_CYBER_ACADEMY_ORIGIN;
}

/** The Enablement tile link: the Cyber Academy site for this deployment (dev, uat or production). */
export function enablementPortalUrl(): string {
  return cyberAcademyOrigin();
}

/**
 * Resolve a Cyber Academy course reference to an absolute URL on the correct origin.
 *
 * - Absolute URLs are re-homed onto the deployment origin only when they point at a Cyber Academy
 *   host (so an unrelated external link is left untouched); the path/query/hash are preserved.
 * - Relative paths (`/course/view.php?id=42`, `course/view.php?id=42`) are joined to the origin.
 * - A bare numeric id or `id=<n>` shorthand resolves to `/course/view.php?id=<n>`.
 * Returns null for an empty reference so callers can hide the embed.
 */
export function cyberAcademyCourseUrl(reference?: string | number | null): string | null {
  if (reference == null) return null;
  const ref = String(reference).trim();
  if (!ref) return null;

  const origin = cyberAcademyOrigin();

  // Bare numeric id → canonical course URL.
  if (/^\d+$/.test(ref)) return `${origin}/course/view.php?id=${ref}`;

  // `id=<n>` shorthand.
  const idMatch = ref.match(/^id=(\d+)$/i);
  if (idMatch) return `${origin}/course/view.php?id=${idMatch[1]}`;

  // Absolute URL.
  if (/^(?:[a-z]+:)?\/\//i.test(ref)) {
    try {
      const parsed = new URL(ref.startsWith('//') ? `https:${ref}` : ref);
      const host = parsed.hostname.toLowerCase();
      if (host.endsWith('cyberacademy.cisco.com')) {
        // Re-home onto the deployment origin, preserving the path/query/hash.
        return `${origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
      return parsed.toString();
    } catch {
      return ref;
    }
  }

  // Relative path.
  const path = ref.startsWith('/') ? ref : `/${ref}`;
  return `${origin}${path}`;
}

/**
 * The Cyber Academy *training* URL for a journey map, by its id — the SE-facing route the courseware
 * is presented under (`/trainings/<id>`), re-homed onto the deployment origin (dev/uat →
 * `https://dev.cyberacademy.cisco.com`, prod → `https://cyberacademy.cisco.com`). The journey map id
 * IS the Cyber Academy training id, so a `journey_map` campaign step links here when no explicit
 * course reference is configured. Returns null for a missing id so callers can hide the link.
 */
export function cyberAcademyTrainingUrl(journeyMapId?: number | string | null): string | null {
  if (journeyMapId == null) return null;
  const id = String(journeyMapId).trim();
  if (!id) return null;
  return `${cyberAcademyOrigin()}/trainings/${id}`;
}

/**
 * Resolve the Cyber Academy course URL a campaign step's *content* is served from.
 *
 * A `journey_map` step references a JourneyMap for completion tracking, but the courseware itself is
 * a Cyber Academy course pointed at by (in priority order) the step's `launch_url`, then a course
 * reference stashed on `provider_config` / `metadata` (`course_url`, `url`, or a bare `course_id`).
 * Each is fed through `cyberAcademyCourseUrl` so a bare id or a prod-host URL is re-homed onto the
 * deployment origin. Returns null when no reference is present, so callers hide the course card.
 */
export function resolveCampaignStepCourseUrl(step: {
  launch_url?: string | null;
  provider_config?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
}): string | null {
  const pc = step.provider_config || {};
  const md = step.metadata || {};
  const ref =
    step.launch_url ||
    pc.course_url ||
    pc.url ||
    pc.course_id ||
    md.course_url ||
    md.url ||
    md.course_id ||
    null;
  return cyberAcademyCourseUrl(ref);
}
